'use client';
import { usePathname } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, ChevronDown, MapPin, Globe } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';

function slugify(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// Global contact numbers shown in the header dropdown
const PHONE_NUMBERS = [
  { country: 'USA', flag: '/flags/us.webp', display: '+1-800-986-4713', tel: '+18009864713' },
  { country: 'Spain', flag: '/flags/es.webp', display: '+34-868-26-9023', tel: '+34868269023' },
  { country: 'Mexico', flag: '/flags/mx.webp', display: '+52-998-609-0037', tel: '+529986090037' },
];

// Fetch locations client-side for the dropdown
function useLocations() {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    fetch('/api/locations')
      .then(r => r.json())
      .then(d => setLocations(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  return locations;
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locDropOpen, setLocDropOpen] = useState(false);
  const [mobileLocOpen, setMobileLocOpen] = useState(false);
  const [phoneDropOpen, setPhoneDropOpen] = useState(false);
  const [mobilePhoneOpen, setMobilePhoneOpen] = useState(false);
  const pathname = usePathname();
  const locations = useLocations();
  const dropRef = useRef(null);
  const phoneDropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setLocDropOpen(false);
      }
      if (phoneDropRef.current && !phoneDropRef.current.contains(e.target)) {
        setPhoneDropOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); setLocDropOpen(false); setPhoneDropOpen(false); }, [pathname]);

  function isActive(href) {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  }

  // Group locations by continent for the dropdown
  const byContinent = {};
  locations.forEach(loc => {
    const key = loc.continent || 'Other';
    if (!byContinent[key]) byContinent[key] = [];
    byContinent[key].push(loc);
  });
  const continentKeys = Object.keys(byContinent);

  const NAV_LEFT = [
    { label: 'About Us', href: '/about-us' },
    { label: 'Contact', href: '/contact-us' },
  ];

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-white/97 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">

        {/* Logo */}
        <div className='logo-head'>
          <Link href="/" aria-label="Go to homepage">
            <Image
              src="/Triplohub.svg"
              alt="Triplohub"
              width={152}
              height={34}
            />
          </Link>
        </div>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6 text-md font-medium text-slate-600">
          {NAV_LEFT.map(({ label, href }) => (
            <Link key={href} href={href}
              className={`relative transition-colors hover:text-sky-500 ${isActive(href) ? 'text-sky-500' : ''}`}>
              {label}
            </Link>
          ))}

          {/* Locations dropdown */}
          <div className="relative" ref={dropRef}>
            <button
              onClick={() => setLocDropOpen(v => !v)}
              onMouseEnter={() => setLocDropOpen(true)}
              className={`flex items-center gap-1 transition-colors hover:text-sky-500 ${
                pathname.includes('/locations') || locations.some(l => pathname.startsWith(`/${l.slug}`))
                  ? 'text-sky-500' : ''
              }`}
            >
              Destinations <ChevronDown size={14} className={`transition-transform ${locDropOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Mega dropdown */}
            {locDropOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden"
                style={{ minWidth: locations.length === 0 ? 240 : Math.min(continentKeys.length * 180 + 40, 900) }}
                onMouseLeave={() => setLocDropOpen(false)}
              >
                {locations.length === 0 ? (
                  <div className="px-6 py-5 text-slate-500 text-sm text-center">
                    No destinations added yet.
                  </div>
                ) : (
                  <div className="flex divide-x divide-slate-100">
                    {continentKeys.map(continent => (
                      <div key={continent} className="py-4 px-5 min-w-40">
                        {/* Continent header */}
                        <div className="flex items-center gap-2 mb-3">
                          <Globe size={13} className="text-sky-500 shrink-0" />
                          <span className="font-bold text-slate-900 text-sm">{continent}</span>
                        </div>
                        <ul className="space-y-0.5">
                          {byContinent[continent].map(loc => (
                            <li key={loc.id} className="mb-1.5">
                              {/* Country link — highlighted as the main entry */}
                              <Link href={`/${loc.slug}`}
                                className="flex items-center gap-1.5 text-slate-900 hover:text-sky-500 text-sm py-1 transition-colors font-bold"
                                onClick={() => setLocDropOpen(false)}>
                                <Globe size={12} className="text-sky-500 shrink-0" />
                                {loc.country}
                              </Link>
                              {/* City sub-links — shown below the country */}
                              {(loc.cities || []).slice(0, 4).map(city => (
                                <Link
                                  key={city.id || city.name}
                                  href={`/${loc.slug}/${slugify(city.name)}`}
                                  className="flex items-center gap-1.5 text-slate-500 hover:text-sky-500 text-xs py-0.5 pl-4 transition-colors"
                                  onClick={() => setLocDropOpen(false)}
                                >
                                  <MapPin size={10} className="text-slate-400 shrink-0" />
                                  {city.name}
                                </Link>
                              ))}
                              {(loc.cities || []).length > 4 && (
                                <Link href={`/${loc.slug}`}
                                  className="block text-sky-400 text-xs py-0.5 pl-4 hover:underline"
                                  onClick={() => setLocDropOpen(false)}>
                                  +{loc.cities.length - 4} more
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Desktop CTA — phone numbers dropdown */}
        <div className="hidden md:flex items-center gap-3">
          <div className="relative" ref={phoneDropRef}>
            <div className="flex items-stretch rounded-full border border-sky-200 hover:border-sky-400 overflow-hidden">
              <a href={`tel:${PHONE_NUMBERS[0].tel}`}
                className="flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700 hover:bg-sky-50 transition-colors pl-4 pr-3 py-2">
                <Image
                  src={PHONE_NUMBERS[0].flag}
                  alt={PHONE_NUMBERS[0].country}
                  width={24}
                  height={24}
                  className="rounded-sm object-cover"
                />
                <Phone size={15} />
                <span>{PHONE_NUMBERS[0].display}</span>
              </a>
              <button
                type="button"
                onClick={() => setPhoneDropOpen(v => !v)}
                aria-label="View more contact numbers"
                aria-expanded={phoneDropOpen}
                className="flex items-center px-2.5 border-l border-sky-200 text-sky-600 hover:text-sky-700 hover:bg-sky-50 transition-colors"
              >
                <ChevronDown size={15} className={`transition-transform ${phoneDropOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {phoneDropOpen && (
              <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden min-w-64">
                <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Call us</p>
                <ul className="py-1">
                  {PHONE_NUMBERS.map(p => (
                    <li key={p.country}>
                      <a
                        href={`tel:${p.tel}`}
                        onClick={() => setPhoneDropOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                      >
                        <Image
                          src={p.flag}
                          alt={p.country}
                          width={24}
                          height={24}
                          className="rounded-sm object-cover flex-shrink-0"
                        />
                        <span className="flex-1">
                          <span className="block font-semibold">{p.country}</span>
                          <span className="block text-slate-500 text-xs">{p.display}</span>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden text-slate-700 p-1" onClick={() => setMobileOpen(v => !v)} aria-label="Toggle menu">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 flex flex-col gap-1 text-sm font-medium text-slate-700 shadow-lg max-h-[80vh] overflow-y-auto">
          {NAV_LEFT.map(({ label, href }) => (
            <Link key={href} href={href} onClick={() => setMobileOpen(false)}
              className={`px-3 py-2.5 rounded-xl transition-colors ${
                isActive(href) ? 'text-sky-500 bg-sky-50 font-semibold' : 'hover:bg-slate-50'
              }`}>
              {label}
            </Link>
          ))}

          {/* Mobile Locations — accordion */}
          <div className="border-t border-slate-100 pt-1 mt-1">
            <button
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 text-left"
              onClick={() => setMobileLocOpen(v => !v)}
            >
              <span className="font-medium text-slate-700">Destinations</span>
              <ChevronDown size={15} className={`text-slate-400 transition-transform ${mobileLocOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileLocOpen && (
              <div className="pl-3 pb-2">
                {locations.length === 0 ? (
                  <p className="text-slate-500 text-xs px-3 py-2">No destinations yet.</p>
                ) : (
                  locations.map(loc => (
                    <div key={loc.id} className="mb-2">
                      <Link href={`/${loc.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-slate-900 font-bold hover:text-sky-500 text-sm">
                        <Globe size={13} className="text-sky-500" />
                        {loc.country}
                      </Link>
                      {(loc.cities || []).map(city => (
                        <Link key={city.id || city.name}
                          href={`/${loc.slug}/${slugify(city.name)}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-1.5 pl-7 py-1.5 text-slate-500 hover:text-sky-500 text-xs">
                          <MapPin size={10} className="text-slate-400" />
                          {city.name}
                        </Link>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="pt-2 mt-1 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setMobilePhoneOpen(v => !v)}
              aria-expanded={mobilePhoneOpen}
              className="flex items-center justify-center gap-2 w-full bg-sky-500 text-white rounded-xl py-3 font-semibold"
            >
              <Image
                  src={PHONE_NUMBERS[0].flag}
                  alt={PHONE_NUMBERS[0].country}
                  width={24}
                  height={24}
                  className="rounded-sm object-cover"
                />
                <Phone size={15} />
                <span>{PHONE_NUMBERS[0].display}</span>
              <ChevronDown size={15} className={`transition-transform ${mobilePhoneOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobilePhoneOpen && (
              <ul className="mt-2 bg-slate-50 rounded-xl overflow-hidden divide-y divide-slate-100">
                {PHONE_NUMBERS.map(p => (
                  <li key={p.country}>
                    <a
                      href={`tel:${p.tel}`}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                    >
                      <Image
                          src={p.flag}
                          alt={p.country}
                          width={24}
                          height={24}
                          className="rounded-sm object-cover flex-shrink-0"
                        />
                      <span className="flex-1">
                        <span className="block font-semibold">{p.country}</span>
                        <span className="block text-slate-500 text-xs">{p.display}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
